import { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";

import { Typography, Box, Grid, makeStyles } from "@material-ui/core";

import { COLORS } from "../../../theme/colors";
import { NoDataCard, Legends } from "../../../components/parts";
import { QuantityChart } from "../../demo/components/QuantityChart";
import {
  getData,
  onlyUniqueValuesOfKeyInArray,
  isObjectEmpty,
  sumFromListOfObjects,
} from "../../../services";

import fiveStarsImg from "../../../assets/images/icons/five-stars.svg";
import fourStarsImg from "../../../assets/images/icons/four-stars.svg";
import threeStarsImg from "../../../assets/images/icons/three-stars.svg";

import diamondImg from "../../../assets/images/icons/diamond.svg";
import starImg from "../../../assets/images/icons/star.svg";
import starOutlinedImg from "../../../assets/images/icons/star-outlined.svg";
import { SolarPotentialPart } from "./SolarPotentialPart";
import { ElectricityEfficiencyPart } from "./ElectricityEfficiencyPart";
import { ElectricityConsumptionPart } from "./ElectricityConsumptionPart";
import { TotalBuildingAreaPart } from "./TotalBuildingAreaPart";
import { TotalRoofAreaPart } from "./TotalRoofAreaPart";

const HOTEL_COLORS = {
  "3 stars": COLORS.blue,
  "4 stars": COLORS.majorelleBlue,
  "5 stars": COLORS.verdigris,
  "5+ stars": COLORS.pearlyPurple,
};

const HOTEL_ICONS = {
  "3 stars": threeStarsImg,
  "4 stars": fourStarsImg,
  "5 stars": fiveStarsImg,
  "5+ stars": fiveStarsImg,
};

const HEALTH_CARE_COLORS = {
  standard: COLORS.majorelleBlue,
  premium: COLORS.verdigris,
  luxury: COLORS.pearlyPurple,
};

const HEALTH_CARE_ICONS = {
  standard: starOutlinedImg,
  premium: starImg,
  luxury: diamondImg,
};

const CLASS_COLORS = {
  ...HOTEL_COLORS,
  ...HEALTH_CARE_COLORS,
};

const HOTEL_BARS = [
  { color: COLORS.blue, key: "3 stars" },
  { color: COLORS.majorelleBlue, key: "4 stars" },
  { color: COLORS.verdigris, key: "5 stars" },
  { color: COLORS.pearlyPurple, key: "5+ stars" },
];

const HEALTH_CARE_BARS = [
  { color: COLORS.majorelleBlue, key: "standard" },
  { color: COLORS.verdigris, key: "premium" },
  { color: COLORS.pearlyPurple, key: "luxury" },
];

const bars = [...HOTEL_BARS, ...HEALTH_CARE_BARS];

const useStyles = makeStyles(() => ({
  divider: {
    height: 15,
    width: 2,
    marginTop: 5,
  },
  chartBox: {
    marginTop: 20,
  },
  title: {
    padding: "10px 0 0 20px",
  },
}));

export const ClassTab = ({ resetSteps }) => {
  const classes = useStyles();
  const { clusters } = useSelector((state) => state.auth);
  const preliminaryStage = clusters[0].preliminary_stage;
  const [electricityCostsChartData, setElectricityCostsChartData] = useState([]);
  const [electricityConsumptionChartData, setElectricityConsumptionChartData] = useState([]);
  const [electricityCostsPerSquareChartData, setElectricityCostsPerSquareChartData] = useState([]);
  const [electricityConsumptionPerSquareChartData, setElectricityConsumptionPerSquareChartData] =
    useState([]);
  const [totalAreaChartData, setTotalAreaChartData] = useState([]);
  const [roofAreaChartData, setRoofAreaChartData] = useState([]);
  const bubbleChartContainerRef = useRef();
  const [quantityChartData, setQuantityChartData] = useState([]);
  const [groupKey, setGroupKey] = useState();
  const [results, setResults] = useState([]);

  useEffect(() => {
    let url = "onboarding/base/";
    let groupKey;

    if (preliminaryStage?.type === "HOTELS" && preliminaryStage?.flexible_category) {
      url += "analytics-by-hotel-classes/";
      groupKey = "hotel_classes";
    } else if (
      preliminaryStage?.type === "HEALTH_CARE_CENTERS" &&
      preliminaryStage?.flexible_category
    ) {
      url += "analytics-by-healthcare-classes/";
      groupKey = "healthcare_classes";
    }

    setGroupKey(groupKey);

    getData(`${url}?clusters=${clusters[0].id}`, (data) => {
      setResults(data);
      const electricity_costs_2021 = {};
      const electricity_costs_2022 = {};
      const electricity_costs_2023 = {};

      const electricity_consumption_2021 = {};
      const electricity_consumption_2022 = {};
      const electricity_consumption_2023 = {};

      const electricity_costs_2021_per_square = {};
      const electricity_costs_2022_per_square = {};
      const electricity_costs_2023_per_square = {};

      const electricity_consumption_2021_per_square = {};
      const electricity_consumption_2022_per_square = {};
      const electricity_consumption_2023_per_square = {};

      const totalAreaChartData = [];
      const roofAreaChartData = [];

      const totalCount = sumFromListOfObjects(data, "count");
      data.forEach((item) => {
        const name = item[groupKey].toLowerCase().replace("-", "_");
        if (item.electricity_costs._2021 !== null)
          electricity_costs_2021[name] = item.electricity_costs._2021;
        if (item.electricity_costs._2022 !== null)
          electricity_costs_2022[name] = item.electricity_costs._2022;
        if (item.electricity_costs._2023 !== null)
          electricity_costs_2023[name] = item.electricity_costs._2023;

        if (item.electricity_consumption._2021 !== null)
          electricity_consumption_2021[name] = item.electricity_consumption._2021;
        if (item.electricity_consumption._2022 !== null)
          electricity_consumption_2022[name] = item.electricity_consumption._2022;
        if (item.electricity_consumption._2023 !== null)
          electricity_consumption_2023[name] = item.electricity_consumption._2023;

        if ("electricity_costs_per_square" in item) {
          if (item.electricity_costs_per_square._2021 !== null)
            electricity_costs_2021_per_square[name] = item.electricity_costs_per_square._2021;
          if (item.electricity_costs_per_square._2022 !== null)
            electricity_costs_2022_per_square[name] = item.electricity_costs_per_square._2022;
          if (item.electricity_costs_per_square._2023 !== null)
            electricity_costs_2023_per_square[name] = item.electricity_costs_per_square._2023;
        }

        if ("electricity_consumption_per_square" in item) {
          if (item.electricity_consumption_per_square?._2021 !== null)
            electricity_consumption_2021_per_square[name] =
              item.electricity_consumption_per_square._2021;
          if (item.electricity_consumption_per_square?._2022 !== null)
            electricity_consumption_2022_per_square[name] =
              item.electricity_consumption_per_square._2022;
          if (item.electricity_consumption_per_square?._2023 !== null)
            electricity_consumption_2023_per_square[name] =
              item.electricity_consumption_per_square._2023;
        }

        totalAreaChartData.push({
          name: item[groupKey],
          value: item.sum_total_square,
          color: CLASS_COLORS[name],
          unit: "unit_system",
        });

        roofAreaChartData.push({
          name: item[groupKey],
          value: item.sum_roof_square,
          color: CLASS_COLORS[name],
          unit: "unit_system",
        });

        item["percent"] = (item.count * 100) / totalCount;
      });

      setQuantityChartData(data);

      if (!onlyUniqueValuesOfKeyInArray(totalAreaChartData, "value", null)) {
        setTotalAreaChartData(totalAreaChartData);
      }
      if (!onlyUniqueValuesOfKeyInArray(roofAreaChartData, "value", null)) {
        setRoofAreaChartData(roofAreaChartData);
      }

      const electricityCosts = [];

      if (!isObjectEmpty(electricity_costs_2021)) {
        electricity_costs_2021["x"] = "2021";
        electricityCosts.push(electricity_costs_2021);
      }

      if (!isObjectEmpty(electricity_costs_2022)) {
        electricity_costs_2022["x"] = "2022";
        electricityCosts.push(electricity_costs_2022);
      }

      if (!isObjectEmpty(electricity_costs_2023)) {
        electricity_costs_2023["x"] = "2023";
        electricityCosts.push(electricity_costs_2023);
      }

      if (electricityCosts.length) {
        setElectricityCostsChartData([
          electricity_costs_2021,
          electricity_costs_2022,
          electricity_costs_2023,
        ]);
      }

      const electricityConsumption = [];
      if (!isObjectEmpty(electricity_consumption_2021)) {
        electricity_consumption_2021["x"] = "2021";
        electricity_consumption_2021["unit"] = "kwh";
        electricityConsumption.push(electricity_consumption_2021);
      }

      if (!isObjectEmpty(electricity_consumption_2022)) {
        electricity_consumption_2022["x"] = "2022";
        electricity_consumption_2022["unit"] = "kwh";
        electricityConsumption.push(electricity_consumption_2022);
      }

      if (!isObjectEmpty(electricity_consumption_2023)) {
        electricity_consumption_2023["x"] = "2023";
        electricity_consumption_2023["unit"] = "kwh";
        electricityConsumption.push(electricity_consumption_2023);
      }

      if (electricityConsumption.length) {
        setElectricityConsumptionChartData([
          electricity_consumption_2021,
          electricity_consumption_2022,
          electricity_consumption_2023,
        ]);
      }

      const electricityCostsPerSquare = [];

      if (!isObjectEmpty(electricity_costs_2021_per_square)) {
        electricity_costs_2021_per_square["x"] = "2021";
        electricityCostsPerSquare.push(electricity_costs_2021_per_square);
      }

      if (!isObjectEmpty(electricity_costs_2022_per_square)) {
        electricity_costs_2022_per_square["x"] = "2022";
        electricityCostsPerSquare.push(electricity_costs_2022_per_square);
      }

      if (!isObjectEmpty(electricity_costs_2023_per_square)) {
        electricity_costs_2023_per_square["x"] = "2023";
        electricityCostsPerSquare.push(electricity_costs_2023_per_square);
      }

      if (electricityCostsPerSquare.length) {
        setElectricityCostsPerSquareChartData([
          electricity_costs_2021_per_square,
          electricity_costs_2022_per_square,
          electricity_costs_2023_per_square,
        ]);
      }

      const electricityConsumptionPerSquare = [];
      if (!isObjectEmpty(electricity_consumption_2021_per_square)) {
        electricity_consumption_2021_per_square["x"] = "2021";
        electricity_consumption_2021_per_square["unit"] = "kwh";
        electricityConsumptionPerSquare.push(electricity_consumption_2021_per_square);
      }

      if (!isObjectEmpty(electricity_consumption_2022_per_square)) {
        electricity_consumption_2022_per_square["x"] = "2022";
        electricity_consumption_2022_per_square["unit"] = "kwh";
        electricityConsumptionPerSquare.push(electricity_consumption_2022_per_square);
      }

      if (!isObjectEmpty(electricity_consumption_2023_per_square)) {
        electricity_consumption_2023_per_square["x"] = "2023";
        electricity_consumption_2023_per_square["unit"] = "kwh";
        electricityConsumptionPerSquare.push(electricity_consumption_2023_per_square);
      }

      if (electricityConsumptionPerSquare.length) {
        setElectricityConsumptionPerSquareChartData([
          electricity_consumption_2021_per_square,
          electricity_consumption_2022_per_square,
          electricity_consumption_2023_per_square,
        ]);
      }
    }).catch((error) => {
      console.log(error);
      if (error?.response?.status === 404) resetSteps();
    });

    return () => {
      setElectricityCostsChartData([]);
      setElectricityConsumptionChartData([]);
      setElectricityCostsPerSquareChartData([]);
      setElectricityConsumptionPerSquareChartData([]);
      setTotalAreaChartData([]);
      setRoofAreaChartData([]);
      setQuantityChartData([]);
    };
  }, []);

  return (
    <Box>
      <Legends
        legends={(preliminaryStage.type === "HOTELS" ? HOTEL_BARS : HEALTH_CARE_BARS).map(
          (bar) => ({
            name: bar.key,
            color: bar.color,
          })
        )}
      />
      <Grid container spacing={2} style={{ marginBottom: 24, marginTop: 14 }}>
        <Grid item xs={4}>
          <Box style={{ backgroundColor: COLORS.darks[0][3], borderRadius: 8, height: "100%" }}>
            <Typography variant="h4" className={classes.title}>
              Quantity
            </Typography>
            <Box
              ref={bubbleChartContainerRef}
              display="flex"
              justifyContent="center"
              alignItems="center"
              className={classes.chartBox}
            >
              {quantityChartData.length ? (
                preliminaryStage?.type === "HOTELS" && preliminaryStage?.flexible_category ? (
                  <QuantityChart
                    data={quantityChartData}
                    containerRef={bubbleChartContainerRef}
                    bubbleKey="hotel_classes"
                    bubbleColors={CLASS_COLORS}
                    icons={HOTEL_ICONS}
                  />
                ) : preliminaryStage?.type === "HEALTH_CARE_CENTERS" &&
                  preliminaryStage?.flexible_category ? (
                  <QuantityChart
                    data={quantityChartData}
                    containerRef={bubbleChartContainerRef}
                    bubbleKey="healthcare_classes"
                    bubbleColors={CLASS_COLORS}
                    icons={HEALTH_CARE_ICONS}
                  />
                ) : null
              ) : (
                <NoDataCard withShaDowBox={false} gridStyle={{ height: 160 }} />
              )}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={4}>
          <TotalBuildingAreaPart totalAreaChartData={totalAreaChartData} />
        </Grid>
        <Grid item xs={4}>
          <TotalRoofAreaPart roofAreaChartData={roofAreaChartData} />
        </Grid>
      </Grid>
      <ElectricityConsumptionPart
        electricityCostsChartData={electricityCostsChartData}
        electricityConsumptionChartData={electricityConsumptionChartData}
        bars={bars}
      />
      <ElectricityEfficiencyPart
        electricityCostsPerSquareChartData={electricityCostsPerSquareChartData}
        electricityConsumptionPerSquareChartData={electricityConsumptionPerSquareChartData}
        bars={bars}
      />
      {results.length ? (
        <SolarPotentialPart groupKey={groupKey} data={results} colors={CLASS_COLORS} />
      ) : null}
    </Box>
  );
};
